import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import i18n from "../../lang/i18n";
import React, { useRef, useState, useEffect, useCallback } from "react";
import Layout from '../../Layout'
import CreateCustomerModal from "../../components/Customer/CreateCustomerModal";
import CreateTaskModal from "../../components/Customer/CreateTaskModal";
import WorkHistoryModal from "../../components/Customer/WorkHistoryModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { parseFilters, buildFilterParams, saveCustomerListQuery, CustomerListFilters } from "./filterState";
import { getChannelCustomers, ChannelCustomerItem, deleteCustomerApi, updateCustomer, CustomerSpam, getCustomerDetail } from "../../api/ChannelCustomer";
import { getNextTags } from "../../utils/appointmentNextTags";
import { extractApiError } from "../../lib/apiError";
import { isEditableFallbackTagName } from "../../lib/customerTag";
import useAuthStore from "../../store/authStore";
import { mixGetV1 } from "../../services/mixin";
import { useDebounce } from "../../hooks/useDebounce";
import { ShieldAlert, ImageIcon } from "lucide-react";
import {
    Table,
    Input,
    Button,
    Tag,
    Avatar,
    Space,
    Modal,
    Checkbox,
    Dropdown,
    message,
    Upload,
    Form,
    Select,
    DatePicker,
    Radio,
    Divider,
    Menu,
    Tooltip,
} from "antd";
import {
    LeftOutlined,
    RightOutlined,
    PlusOutlined,
    AppstoreOutlined,
    SyncOutlined,
    SettingOutlined,
    SearchOutlined,
    ReloadOutlined,
    FileAddOutlined,
    DownloadOutlined,
    InboxOutlined,
    CheckOutlined,
    LoadingOutlined,
    CalendarOutlined,
    UserOutlined,
    MoreOutlined,
    ShoppingCartOutlined,
    MessageOutlined,
    DeleteOutlined,
    ExclamationCircleFilled,
    StarFilled,
    FileTextOutlined,
    EditOutlined,
} from "@ant-design/icons";
import type { MenuProps, UploadProps } from "antd";
import { Image } from "antd";
import Toolbar from "components/Customer/Toolbar";
import StatusTabs from "components/Customer/StatusTabs";
import useTableColumnSettings, { SettingColumn } from "hooks/useTableColumnSettings";
import { ColumnSettingButton } from "components/TableColumnSetting";
import dayjs from "dayjs";

/* ================= TYPES ================= */

interface FilterItem {
    key: string;
    label: string;
    count: number;
    color: string;
}

/* ================= FILTER DATA ================= */

/* ================= COMPONENT ================= */

// Cột checkbox chọn dòng: cố định độ rộng để tính đúng tổng chiều rộng bảng khi người dùng kéo cột
const SELECTION_COL_WIDTH = 48;

// Kênh liên kết (connections[].source / channel_type_code)
const CHANNEL_META: Record<string, { label: string; color: string }> = {
    fb: { label: "Facebook", color: "#1877F2" },
    facebook: { label: "Facebook", color: "#1877F2" },
    insta: { label: "Instagram", color: "#E1306C" },
    instagram: { label: "Instagram", color: "#E1306C" },
    zalo: { label: "Zalo", color: "#0068FF" },
    tiktok: { label: "TikTok", color: "#111111" },
};

// Nguồn khách hàng (customer.source)
const SOURCE_LABELS: Record<string, string> = {
    facebook: "Facebook",
    fb: "Facebook",
    zalo: "Zalo",
    tiktok: "TikTok",
    insta: "Instagram",
    instagram: "Instagram",
    manual: "Nhập tay",
};

const EmptyCell = () => <span style={{ color: "#aaa" }}>—</span>;

const formatDateTime = (value?: string | null, lang = "vi") => {
    if (!value) return null;
    const d = dayjs(value);
    if (!d.isValid()) return null;
    return d.format(lang === "vi" ? "DD/MM/YYYY HH:mm" : "MM/DD/YYYY HH:mm");
};

const CustomerCare: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { isSuperAdmin, hasPermission } = useAuthStore();

    const filters: FilterItem[] = [
        { key: "consulting", label: t('customer.consulting'), count: 2, color: "#13c2c2" },
        { key: "cancel", label: t('customer.cancel'), count: 1, color: "#ff4d4f" },
        { key: "purchased", label: t('customer.purchased'), count: 2, color: "#1677ff" },
        { key: "care2", label: t('customer.care_round_2'), count: 0, color: "#fa8c16" },
        { key: "care1", label: t('customer.care_round_1'), count: 1, color: "#ff7875" },
        { key: "new", label: t('customer.new_customer'), count: 1, color: "#722ed1" },
    ];
    const [activeFilter, setActiveFilter] = useState("purchased");

    // Đọc bộ lọc từ URL một lần lúc mount. Các lần ghi URL sau đó không được
    // đọc lại, nếu không state và URL sẽ đá nhau thành vòng lặp.
    const [searchParams, setSearchParams] = useSearchParams();
    const initialFiltersRef = useRef<CustomerListFilters | null>(null);
    if (initialFiltersRef.current === null) {
        initialFiltersRef.current = parseFilters(searchParams);
    }
    const initialFilters = initialFiltersRef.current;

    const [customerCareStatusId, setCustomerCareStatusId] = useState<number | null>(initialFilters.tagId);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [uploading, setUploading] = useState(false);
    const [openCreate, setOpenCreate] = useState(false);
    const [openTask, setOpenTask] = useState(false);
    const [openHistory, setOpenHistory] = useState(false);
    const [historyCustomerId, setHistoryCustomerId] = useState<number | null>(null);
    const [openEdit, setOpenEdit] = useState(false);
    const navigate = useNavigate();
    const [deleteCustomer, setDeleteCustomer] = useState<ChannelCustomerItem | null>(null);
    const [openSpamTooltip, setOpenSpamTooltip] = useState(false);

    const [openSpamTooltipId, setOpenSpamTooltipId] = useState<number | null>(null);

    const [openSpamModal, setOpenSpamModal] = useState(false);

    const [selectSpam, setSelectSpam] = useState<CustomerSpam | null>(null)

    const spamImages = [
        "https://picsum.photos/900/600?1",
        "https://picsum.photos/900/600?2",
        "https://picsum.photos/900/600?3",
    ];

    /* ===== Đổi thẻ phân loại KH ngay trên danh sách =====
       Dùng đúng nguồn dữ liệu và quy tắc của trang chi tiết khách hàng:
       chọn thẻ trong nhóm group_1, thẻ ngoài nhóm chỉ hiển thị tĩnh (trừ
       "Mới - Tiếp cận" và "KNC - Sai thông tin..."), lưu qua customer_tag_id. */
    const canEditCustomerTag = isSuperAdmin || hasPermission("customer.edit");
    const [tagOptions, setTagOptions] = useState<{ value: number; label: string; color?: string }[]>([]);
    const [updatingTagRowId, setUpdatingTagRowId] = useState<number | null>(null);

    useEffect(() => {
        if (!canEditCustomerTag) return;
        mixGetV1('/api/v1/be/customer-tags', { limit: 100, sort: ["id,desc"], group: 'group_1' })
            .then((res) => {
                if (res?.status && res.data?.items) {
                    setTagOptions(res.data.items.map((tag: any) => ({
                        value: tag.id,
                        label: tag.name,
                        color: tag.color || "#1890ff",
                    })));
                }
            })
            .catch(() => { /* không có thẻ để chọn thì cột vẫn hiển thị tĩnh */ });
    }, [canEditCustomerTag]);

    const handleChangeCustomerTag = async (record: ChannelCustomerItem, tagId: number) => {
        if (record.customer_tag?.id === tagId) return;
        setUpdatingTagRowId(record.id);
        // API yêu cầu kèm display_name khi cập nhật (giống trang chi tiết)
        const res = await updateCustomer(record.id, {
            display_name: record.display_name,
            customer_tag_id: tagId,
        });
        setUpdatingTagRowId(null);
        if (!res.status) {
            message.error(extractApiError(res, i18n.language) || t("roomchat.update_failed") || "Cập nhật thất bại");
            return;
        }
        if (Array.isArray(record.dynamic_next_tags)) {
            // Theo ma trận: thẻ tiếp theo đổi theo thẻ mới -> lấy lại từ chi tiết khách
            const detail = await getCustomerDetail(record.id);
            const next = getNextTags(record).find((o) => o.id === tagId);
            updateRow(record.id, {
                customer_tag: detail.data?.customer_tag
                    ?? (next ? { id: next.id, name: next.name, color: next.color } : record.customer_tag),
                dynamic_next_tags: detail.data ? getNextTags(detail.data) : [],
            });
        } else {
            const opt = tagOptions.find((o) => o.value === tagId);
            updateRow(record.id, {
                customer_tag: opt ? { id: opt.value, name: opt.label, color: opt.color } : record.customer_tag,
            });
        }
        // Số lượng trên các chip lọc theo thẻ đã đổi -> đếm lại
        setStatusRefreshKey((k) => k + 1);
        message.success(t("roomchat.update_success") || "Cập nhật thành công");
    };

    /* ================= TABLE COLUMNS ================= */

    const columns: SettingColumn<ChannelCustomerItem>[] = [
        // {
        //     key: "index",
        //     title: "#",
        //     render: (_, __, index) => index + 1,
        //     width: 60,
        // },
        {
            key: "customerName",
            title: t('customer.col_customer_name'),
            render: (_, record) => {
                const logs = record?.spam_logs ?? [];

                const latestSpam =
                    logs.length > 0
                    ? logs.reduce((latest, current) =>
                        new Date(current.created_at) > new Date(latest.created_at)
                            ? current
                            : latest
                        )
                    : null;
                return (
                     // khi co canh bao spam thi add class mess-spam
                    <div className="mess-spam" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ position: 'relative', cursor: "pointer" }} onClick={() => navigate(`/customer/${record.id}`)}>
                            <Avatar src={record.avatar || record.avatar_url}>{record.display_name?.charAt(0)}</Avatar>
                            {record.is_priority_contact && (
                                <StarFilled style={{ position: 'absolute', top: -4, right: -4, color: '#faad14', fontSize: 14 }} />
                            )}
                        </div>

                        <div style={{ flex: 1 }}>
                            <div
                                className="name-user flex items-center gap-1"
                                style={{ fontWeight: 600, cursor: "pointer" }}
                            >
                                <span
                                    className="cursor-pointer"
                                    onClick={() => navigate(`/customer/${record.id}`)}
                                >
                                    {record.display_name}
                                </span>
                                {record.is_spam === 1 && (
                                    <Tooltip
                                        open={openSpamTooltipId === record.id}
                                        onOpenChange={(visible) => {
                                            if (!openSpamModal) {
                                                setOpenSpamTooltipId(
                                                    visible ? record.id : null
                                                );
                                            }
                                        }}
                                        placement="topLeft"
                                        color="#fff"
                                        overlayInnerStyle={{
                                            padding: 0,
                                            borderRadius: 16,
                                        }}
                                        title={
                                            <div className="w-[340px] overflow-hidden rounded-xl border border-[#fecaca] bg-white">
                                                {/* Header */}
                                                <div className="flex items-center gap-2 border-b border-[#fee2e2] bg-[#fff5f5] px-4 py-3">
                                                    <ShieldAlert className="h-4 w-4 text-[#dc2626]" />

                                                    <span className="text-[16px] font-semibold text-[#dc2626]">
                                                        Chi tiết cảnh báo Spam
                                                    </span>
                                                </div>

                                                {/* Body */}
                                                <div className="space-y-5 p-4">
                                                    {/* Nhân viên note */}
                                                    <div>
                                                        <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-slate-400">
                                                            Nhân viên note
                                                        </div>

                                                        <div className="flex items-center gap-2 text-[14px] font-medium text-slate-700">
                                                            <UserOutlined className="text-[#3e91ab]" />
                                                            { latestSpam?.creator?.fullname }
                                                        </div>
                                                    </div>

                                                    {/* Nguyên nhân */}
                                                    <div>
                                                        <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-slate-400">
                                                            Nguyên nhân
                                                        </div>

                                                        <div className="text-[14px] leading-7 text-slate-700">
                                                            { latestSpam?.note }
                                                        </div>
                                                    </div>

                                                    {/* Bằng chứng */}
                                                    <div>
                                                        <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-slate-400">
                                                            Bằng chứng
                                                        </div>

                                                        <div
                                                            onClick={(e) => {
                                                                e.stopPropagation();

                                                                setOpenSpamTooltipId(null);
                                                                setOpenSpamModal(true);
                                                                setSelectSpam(latestSpam)
                                                            }}
                                                            className="
                                                                flex h-[120px] cursor-pointer items-center justify-center
                                                                rounded-lg border border-[#fee2e2]
                                                                bg-[#fff1f2]
                                                                transition-all hover:bg-[#ffe4e6]
                                                            "
                                                        >
                                                            <div className="text-center">
                                                                <ImageIcon className="mx-auto mb-2 h-7 w-7 text-[#dc2626]" />

                                                                <div className="font-semibold text-[#dc2626]">
                                                                    Xem Ảnh Bằng Chứng
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    >
                                        <div>
                                            <ShieldAlert className="h-4 w-4 cursor-pointer text-[#dc2626]" />
                                        </div>
                                    </Tooltip>
                                )}
                            </div>
                            {/* Họ tên thật (fullname) — chỉ hiện khi có và khác tên hiển thị */}
                            {record.fullname && record.fullname !== record.display_name && (
                                <div className="text-gray-500 dark:text-gray-400" style={{ fontSize: 12 }}>
                                    {record.fullname}
                                </div>
                            )}
                            <div className="flex gap-2 mt-[2px]" style={{ fontSize: 12, color: "#3e91ab", cursor: "pointer" }} onClick={() => navigate(`/customer/${record.id}`)}>
                                {record.phone}
                                {record.is_spam === 1 && (
                                    <Tag
                                        color="#fff1f0"
                                        style={{
                                            color: "#dc2626",
                                            borderColor: "#fee2e2",
                                            borderRadius: 4,
                                            fontSize: 12,
                                            padding: "0 8px",
                                            lineHeight: "20px",
                                        }}
                                    >
                                        Cảnh báo Spam
                                    </Tag>
                                )}
                            </div>
                        </div>

                        <div style={{ display: "flex", gap: 10 }}>
                            <Tooltip title={t('customer.work_history')}>
                                <FileTextOutlined
                                    style={{ cursor: "pointer", color: "#666" }}
                                    onClick={() => { setHistoryCustomerId(record.id); setOpenHistory(true); }}
                                />
                            </Tooltip>

                            <Tooltip title={t('customer.customer_details')}>
                                <EditOutlined
                                    style={{ cursor: "pointer", color: "#666" }}
                                    onClick={() => navigate(`/customer/${record.id}`)}
                                />
                            </Tooltip>
                        </div>
                    </div>
                )
            },
        },
        /* ----- Các cột theo "Frontend Column Mapping" của API /customers -----
           Cột ít dùng để defaultHidden: người dùng tự bật trong cài đặt cột. */
        {
            key: "customerCode",
            title: t('customer.col_customer_code'),
            width: 120,
            render: (_, record) => record.customer_code
                ? <span className="font-semibold cursor-pointer text-[#3e91ab]" onClick={() => navigate(`/customer/${record.id}`)}>{record.customer_code}</span>
                : <EmptyCell />,
        },
        {
            key: "fullname",
            title: t('customer.col_fullname'),
            defaultHidden: true,
            render: (_, record) => record.fullname || <EmptyCell />,
        },
        {
            // Số chính + các số phụ (phones[]) dạng badge nhỏ
            key: "phones",
            title: t('customer.col_phone'),
            defaultHidden: true,
            render: (_, record) => {
                const extra = (record.phones || []).filter((p) => p.phone && p.phone !== record.phone);
                if (!record.phone && !extra.length) return <EmptyCell />;
                return (
                    <div className="flex flex-col gap-1">
                        {record.phone && <span>{record.phone}</span>}
                        {extra.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {extra.map((p) => (
                                    <Tooltip key={p.id} title={p.note || undefined}>
                                        <Tag className="mr-0 text-[11px]">{p.phone}</Tag>
                                    </Tooltip>
                                ))}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            key: "email",
            title: "Email",
            defaultHidden: true,
            render: (_, record) => record.email || <EmptyCell />,
        },
        {
            key: "taxCode",
            title: t('customer.col_tax_code'),
            defaultHidden: true,
            render: (_, record) => record.tax_code || <EmptyCell />,
        },
        {
            key: "birthday",
            title: t('customer.col_birthday'),
            defaultHidden: true,
            width: 150,
            render: (_, record) => {
                const d = record.birthday ? dayjs(record.birthday) : null;
                if (!d || !d.isValid()) return <EmptyCell />;
                const age = dayjs().diff(d, 'year');
                return (
                    <span>
                        {d.format('DD/MM/YYYY')}
                        <span className="text-gray-400 ml-1">({age} {t('customer.age_suffix')})</span>
                    </span>
                );
            },
        },
        {
            key: "gender",
            title: t('customer.col_gender'),
            defaultHidden: true,
            width: 100,
            render: (_, record) => {
                const map: Record<number, string> = {
                    0: t('customer.gender_female'),
                    1: t('customer.gender_male'),
                    2: t('customer.gender_other'),
                };
                return record.gender !== null && record.gender !== undefined && map[record.gender]
                    ? map[record.gender]
                    : <EmptyCell />;
            },
        },
        {
            key: "createdAt",
            title: t('customer.col_created_at'),
            width: 180,
            render: (_, record) => formatDateTime(record.created_at, i18n.language) || "—",
        },
        {
            key: "updatedAt",
            title: t('customer.col_updated_at'),
            defaultHidden: true,
            width: 180,
            render: (_, record) => formatDateTime(record.updated_at, i18n.language) || <EmptyCell />,
        },
        {
            key: "address",
            title: t('customer.col_address'),
            defaultHidden: true,
            render: (_, record) => record.address || <EmptyCell />,
        },
        {
            key: "province",
            title: t('customer.col_province'),
            render: (_, record) => record.province?.name || "—",
        },
        {
            key: "ward",
            title: t('customer.col_ward'),
            render: (_, record) => record.ward?.name || "—",
        },
        {
            key: "isForeign",
            title: t('customer.col_origin'),
            defaultHidden: true,
            // Đủ chỗ cho tag "Nước ngoài / Việt kiều" khi bảng chuyển sang layout fixed (đã kéo độ rộng)
            width: 180,
            render: (_, record) => record.is_foreign === 1
                ? <Tag color="purple" className="mr-0">{t('customer.origin_foreign')}</Tag>
                : <span>{t('customer.origin_domestic')}</span>,
        },
        {
            key: "source",
            title: t('customer.col_source'),
            defaultHidden: true,
            render: (_, record) => record.source
                ? (SOURCE_LABELS[record.source.toLowerCase()] || record.source)
                : <EmptyCell />,
        },
        {
            // Kênh mạng xã hội đã liên kết — gộp trùng theo loại kênh
            key: "connections",
            title: t('customer.col_connections'),
            render: (_, record) => {
                const codes = Array.from(new Set(
                    (record.connections || [])
                        .map((c) => (c.channel_connection?.channel_type_code || c.source || '').toLowerCase())
                        .filter(Boolean)
                ));
                if (!codes.length) return <EmptyCell />;
                return (
                    <div className="flex flex-wrap gap-1">
                        {codes.map((code) => {
                            const meta = CHANNEL_META[code] || { label: code, color: "#8c8c8c" };
                            const pages = (record.connections || [])
                                .filter((c) => (c.channel_connection?.channel_type_code || c.source || '').toLowerCase() === code)
                                .map((c) => c.channel_connection?.display_name)
                                .filter(Boolean);
                            return (
                                <Tooltip key={code} title={pages.length ? Array.from(new Set(pages)).join(', ') : undefined}>
                                    <Tag color={meta.color} className="mr-0">{meta.label}</Tag>
                                </Tooltip>
                            );
                        })}
                    </div>
                );
            },
        },
        {
            key: "careStatus",
            title: t('customer.col_classification'),
            width: 220,
            render: (_, record) => {
                const tag = record.customer_tag;
                const saving = updatingTagRowId === record.id;
                // Ngữ cảnh "Khách hàng" (screen_context = 4) của ma trận: dùng `dynamic_next_tags`
                // backend trả theo từng dòng. API chưa trả trường này thì giữ luồng cũ (nhóm group_1).
                const useDynamicTags = Array.isArray(record.dynamic_next_tags);
                const rowTagOptions = useDynamicTags
                    ? getNextTags(record).map((t) => ({ value: t.id, label: t.name, color: t.color || "#1890ff" }))
                    : tagOptions;
                // Thẻ ngoài nhóm group_1 chỉ hiển thị tĩnh, trừ vài thẻ đặc biệt (giống trang chi tiết)
                const tagInList = tag ? tagOptions.some((o) => o.value === tag.id) : false;
                const editable = canEditCustomerTag
                    && rowTagOptions.length > 0
                    && (useDynamicTags || !tag || tagInList || isEditableFallbackTagName(tag.name));

                if (!tag) {
                    return editable ? (
                        <Select
                            size="small"
                            placeholder={t('cd.select_tag')}
                            loading={saving}
                            className="w-[160px]"
                            options={rowTagOptions.map((opt) => ({
                                value: opt.value,
                                label: <Tag color={opt.color} className="mr-0">{opt.label}</Tag>,
                            }))}
                            onChange={(val) => handleChangeCustomerTag(record, val)}
                            value={null}
                        />
                    ) : <span style={{ color: "#aaa" }}>—</span>;
                }

                const tagEl = <Tag color={tag.color || undefined} className="mr-0">{tag.name}</Tag>;
                if (!editable) return tagEl;

                return (
                    <div className="flex items-center gap-1">
                        {tagEl}
                        <Dropdown
                            trigger={["click"]}
                            disabled={saving}
                            menu={{
                                selectable: false,
                                items: rowTagOptions.map((opt) => ({
                                    key: opt.value,
                                    label: (
                                        <div className="flex items-center justify-between min-w-[140px]">
                                            <Tag color={opt.color} className="mr-0">{opt.label}</Tag>
                                            {tag.id === opt.value && (
                                                <CheckOutlined className="text-green-500 ml-2" />
                                            )}
                                        </div>
                                    ),
                                })),
                                onClick: ({ key, domEvent }) => {
                                    domEvent.stopPropagation();
                                    handleChangeCustomerTag(record, Number(key));
                                },
                            }}
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={saving
                                    ? <LoadingOutlined />
                                    : <SyncOutlined className="text-gray-400 hover:text-blue-500" />}
                            />
                        </Dropdown>
                    </div>
                );
            },
        },
        {
            // API trả cả hai vai trò kèm alias cũ: in_charge_staff/staff (trực page)
            // và contact_staff/telesale (telesale)
            key: "pageStaff",
            title: t('customer.page_staff'),
            render: (_, record) => {
                const name = record.in_charge_staff?.name || record.staff?.name;
                return name
                    ? <span style={{ fontWeight: 500 }}>{name}</span>
                    : <span style={{ color: "#aaa" }}>—</span>;
            },
        },
        {
            key: "saleEmployee",
            title: t('customer.telesale_staff'),
            render: (_, record) => {
                const name = record.contact_staff?.name || record.telesale?.name;
                return name
                    ? <span style={{ fontWeight: 500 }}>{name}</span>
                    : <span style={{ color: "#aaa" }}>—</span>;
            },
        },
        {
            key: "careStatuses",
            title: t('customer.col_care_statuses'),
            render: (_, record) => {
                const list = record.care_statuses || [];
                if (!list.length) return <EmptyCell />;
                return (
                    <div className="flex flex-wrap gap-1">
                        {list.map((s) => (
                            <Tag key={s.id} color={s.color || undefined} className="mr-0">{s.name}</Tag>
                        ))}
                    </div>
                );
            },
        },
        {
            key: "isSpam",
            title: "Spam",
            defaultHidden: true,
            width: 100,
            render: (_, record) => record.is_spam === 1
                ? <Tag color="red" className="mr-0">Spam</Tag>
                : <CheckOutlined className="text-green-500" />,
        },
        {
            key: "hasChat",
            title: t('customer.col_has_chat'),
            defaultHidden: true,
            width: 120,
            align: "center",
            render: (_, record) => record.has_chat_customer === 1
                ? <Tooltip title={t('customer.has_chat_yes')}><MessageOutlined className="text-[#3e91ab]" /></Tooltip>
                : <EmptyCell />,
        },
        {
            key: "note",
            title: t('customer.col_note'),
            defaultHidden: true,
            width: 240,
            render: (_, record) => record.note
                ? (
                    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{record.note}</span>}>
                        <div className="line-clamp-2 text-gray-600 dark:text-gray-300" style={{ maxWidth: 240 }}>{record.note}</div>
                    </Tooltip>
                )
                : <EmptyCell />,
        },
        {
            key: "unreadCount",
            title: t('customer.col_unread'),
            width: 100,
            render: (_, record) => record.unread_count > 0
                ? <Tag color="red">{record.unread_count}</Tag>
                : "0",
        },
        {
            key: "actions",
            title: t('customer.col_actions'),
            align: "center",
            width: 120,
            fixed: "right",
            lockVisible: true,
            render: (_, record: any) => {
                const items = [
                    {
                        key: "detail",
                        label: (
                            <div className="flex items-center gap-2">
                                <UserOutlined />
                                <span>{t('customer.customer_details')}</span>
                            </div>
                        ),
                        onClick: () =>
                            navigate(`/customer/${record.id}`),
                    },
                    {
                        key: "order",
                        label: (
                            <div className="flex items-center gap-2">
                                <ShoppingCartOutlined />
                                <span>{t('customer.create_order')}</span>
                            </div>
                        ),
                        onClick: () =>
                            navigate(`/appointment/create?customer_id=${record.id}`),
                    },
                    {
                        key: "conversation",
                        label: (
                            <div className="flex items-center gap-2">
                                <MessageOutlined />
                                <span>{t('customer.view_conversation')}</span>
                            </div>
                        ),
                        onClick: () =>
                            navigate(`/chat/${record.connections[0].channel_customer.connection.id}?customer_id=${record.connections[0].channel_customers_id}`),
                    },
                    {
                        key: "delete",
                        danger: true,
                        label: (
                            <div className="flex items-center gap-2">
                                <DeleteOutlined />
                                <span>{t('customer.delete_customer')}</span>
                            </div>
                        ),
                        onClick: () => setDeleteCustomer(record),
                    },
                ];

                return (
                    <Dropdown
                        trigger={["click"]}
                        menu={{ items }}
                        placement="bottomRight"
                    >
                        {/* Icon ba chấm: thể hiện menu hành động, tránh nhầm với "xem hồ sơ" */}
                        <MoreOutlined
                            title={t('customer.col_actions')}
                            style={{
                                cursor: "pointer",
                                fontSize: 18,
                                color: "#666",
                                padding: 4,
                            }}
                        />
                    </Dropdown>
                );
            },
        }
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings("customer_list", columns, { extraWidth: SELECTION_COL_WIDTH });

    const rowSelection = {
        columnWidth: SELECTION_COL_WIDTH,
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
        },
    };

    const scroll = (direction: "left" | "right") => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollBy({
            left: direction === "left" ? -200 : 200,
            behavior: "smooth",
        });
    };

    /* ================= DATA ================= */

    const [data, setData] = useState<ChannelCustomerItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState(initialFilters.search);
    const [page, setPage] = useState(initialFilters.page);
    const [total, setTotal] = useState(0);
    const [staffId, setStaffId] = useState<number | undefined>(initialFilters.staffId);
    // Nhân viên trực page phụ trách khách (in_charge_staff_id)
    const [pageStaffId, setPageStaffId] = useState<number | undefined>(initialFilters.pageStaffId);
    const [startDate, setStartDate] = useState<string | undefined>(initialFilters.startDate);
    const [endDate, setEndDate] = useState<string | undefined>(initialFilters.endDate);
    const [oauthAccountIds, setOauthAccountIds] = useState<number[]>(initialFilters.oauthAccountIds);
    const [channelConnectionIds, setChannelConnectionIds] = useState<number[]>(initialFilters.channelConnectionIds);
    const [careStatusIds, setCareStatusIds] = useState<number[]>(initialFilters.careStatusIds);
    // Chỉ dùng để dựng lại DatePicker khi quay lại, không tham gia gọi API
    const [period, setPeriod] = useState<string>(initialFilters.period);
    const [refDate, setRefDate] = useState<string | undefined>(initialFilters.refDate);
    const debouncedSearch = useDebounce(searchText, 400);

    // Đồng bộ bộ lọc lên URL (replace để không đẻ thêm history mỗi lần gõ),
    // đồng thời lưu lại cho nút quay lại ở trang chi tiết dùng.
    useEffect(() => {
        const params = buildFilterParams({
            search: debouncedSearch,
            tagId: customerCareStatusId,
            page,
            staffId,
            pageStaffId,
            startDate,
            endDate,
            period,
            refDate,
            oauthAccountIds,
            channelConnectionIds,
            careStatusIds,
        });
        const qs = params.toString();
        saveCustomerListQuery(qs);
        setSearchParams(params, { replace: true });
    }, [
        setSearchParams,
        debouncedSearch,
        customerCareStatusId,
        page,
        staffId,
        pageStaffId,
        startDate,
        endDate,
        period,
        refDate,
        oauthAccountIds,
        channelConnectionIds,
        careStatusIds,
    ]);

    const fetchCustomers = useCallback(async (
        search?: string,
        statusId?: number | null,
        currentPage = 1,
        staff?: number,
        pageStaff?: number,
        start?: string,
        end?: string,
        oauthIds?: number[],
        connectionIds?: number[],
        careIds?: number[],
    ) => {
        setLoading(true);
        const res = await mixGetV1('/api/v1/be/customers', {
            search: search || undefined,
            customer_tag_id: statusId || undefined,
            contact_staff_id: staff || undefined,
            in_charge_staff_id: pageStaff || undefined,
            start_date: start || undefined,
            end_date: end || undefined,
            oauth_account_id: oauthIds && oauthIds.length ? oauthIds : undefined,
            channel_connection_id: connectionIds && connectionIds.length ? connectionIds : undefined,
            customer_care_status_ids: careIds && careIds.length ? careIds : undefined,
            page: currentPage,
        });
        if (res.status && res.data) {
            setData(res.data.items);
            setTotal(res.data.total || 0);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchCustomers(debouncedSearch, customerCareStatusId, page, staffId, pageStaffId, startDate, endDate, oauthAccountIds, channelConnectionIds, careStatusIds);
    }, [fetchCustomers, debouncedSearch, customerCareStatusId, page, staffId, pageStaffId, startDate, endDate, oauthAccountIds, channelConnectionIds, careStatusIds]);

    const handleSearch = useCallback((value: string) => {
        setPage(1);
        setSearchText(value);
    }, []);

    const handleStaffChange = useCallback((value?: number) => {
        setPage(1);
        setStaffId(value);
    }, []);

    const handlePageStaffChange = useCallback((value?: number) => {
        setPage(1);
        setPageStaffId(value);
    }, []);

    const handleDateChange = useCallback((start?: string, end?: string, nextPeriod?: string, nextRefDate?: string) => {
        setPage(1);
        setStartDate(start);
        setEndDate(end);
        if (nextPeriod) setPeriod(nextPeriod);
        setRefDate(nextRefDate);
    }, []);

    const handleSourceChange = useCallback((oauthIds: number[], connectionIds: number[]) => {
        setPage(1);
        setOauthAccountIds(oauthIds);
        setChannelConnectionIds(connectionIds);
    }, []);

    const handleCareStatusChange = useCallback((ids: number[]) => {
        setPage(1);
        setCareStatusIds(ids);
    }, []);

    const [deleteLoading, setDeleteLoading] = useState(false);
    // Tăng để buộc StatusTabs đếm lại số lượng theo tag sau khi xóa/tạo khách
    const [statusRefreshKey, setStatusRefreshKey] = useState(0);

    const updateRow = (id: number, newData: Partial<ChannelCustomerItem>) => {
        setData((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, ...newData } : item
            )
        );
    };

    const uploadMenu: MenuProps["items"] = [
        {
            key: "download-template",
            label: (
                <div className="flex items-center gap-2">
                    <DownloadOutlined />
                    <span>{t('customer.download_template')}</span>
                </div>
            ),
        },
        {
            key: "select-file",
            label: (
                <div className="flex items-center gap-2">
                    <FileAddOutlined />
                    <span>{t('customer.select_file_upload')}</span>
                </div>
            ),
        },
    ];

    const uploadProps: UploadProps = {
        name: "file",
        multiple: false,
        accept: ".xlsx,.xls",
        showUploadList: false,

        beforeUpload: (file) => {
            const isExcel =
                file.type ===
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                file.type === "application/vnd.ms-excel";

            if (!isExcel) {
                message.error(t('customer.excel_only'));
                return Upload.LIST_IGNORE;
            }

            setUploading(true);

            // Giả lập API upload
            setTimeout(() => {
                setUploading(false);
                message.success(t('customer.upload_success'));
            }, 1500);

            return false; // chặn auto upload
        },
    };

    const handleDownloadTemplate = () => {
        const link = document.createElement("a");
        link.href = "/template/customer_template.xlsx"; // đổi path nếu cần
        link.download = "customer_template.xlsx";
        link.click();
    };

    const handleDeleteCustomer = async () => {
    const customerId = deleteCustomer?.customer?.id ?? deleteCustomer?.id;
    if (!customerId) return;
    setDeleteLoading(true);
    const res = await deleteCustomerApi(customerId);
    setDeleteLoading(false);
    if (res.status) {
        message.success(t('customer.delete_success'));
        setDeleteCustomer(null);
        fetchCustomers(searchText, customerCareStatusId, page);
        setStatusRefreshKey((k) => k + 1);
    } else {
        // Hiển thị đúng lỗi API trả về (map theo ngôn ngữ đang chọn), fallback thông báo chung
        message.error(extractApiError(res, i18n.language) || t('customer.delete_failed'));
    }
};

    /* ================= RENDER ================= */

    return (
        <Layout>
            <Helmet>
                <title>{t('page.customers')} | Khothi</title>
            </Helmet>
            <h2 className="text-lg font-semibold t-text-white mb-4">
                {t('customer.title')}
            </h2>
            <div>
                {/* SEARCH + TOOL */}
                <Toolbar
                    onCreate={() => setOpenCreate(true)}
                    onSearch={handleSearch}
                    onStaffChange={handleStaffChange}
                    onPageStaffChange={handlePageStaffChange}
                    onDateChange={handleDateChange}
                    onSourceChange={handleSourceChange}
                    onCareStatusChange={handleCareStatusChange}
                    initialFilters={initialFilters}
                />

                {/* FILTER BAR + CÀI ĐẶT CỘT */}
                <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                        <StatusTabs
                            activeTagId={customerCareStatusId}
                            onFilterChange={(statusId) => setCustomerCareStatusId(statusId)}
                            refreshKey={statusRefreshKey}
                            contactStaffId={staffId}
                            inChargeStaffId={pageStaffId}
                            search={debouncedSearch}
                            startDate={startDate}
                            endDate={endDate}
                            oauthAccountIds={oauthAccountIds}
                            channelConnectionIds={channelConnectionIds}
                            careStatusIds={careStatusIds}
                        />
                    </div>
                    <ColumnSettingButton {...columnSettingProps} />
                </div>

                {/* TABLE */}
                <Table
                    rowKey="id"
                    loading={loading}
                    rowSelection={rowSelection}
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    dataSource={data}
                    pagination={{
                        current: page,
                        pageSize: 20,
                        total: total,
                        showSizeChanger: false,
                        onChange: (p) => setPage(p),
                    }}
                    scroll={{ x: scrollX }}
                    className="custom-ant-table"
                />

                <Modal
                    open={!!deleteCustomer}
                    onCancel={() => setDeleteCustomer(null)}
                    footer={null}
                    width={480}
                    className="
                        dark:[&_.ant-modal-content]:bg-[#1f1f1f]
                        dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                    "
                >
                    <div className="flex gap-4">
                        <ExclamationCircleFilled
                            style={{ fontSize: 24, color: "#faad14" }}
                        />

                        <div>
                            <div className="text-lg font-semibold mb-2 t-text-white">
                                {t('customer.delete_customer')}
                            </div>

                            <div className="text-gray-600 text-000000e0 mb-6">
                                {t('customer.confirm_delete')} "
                                {deleteCustomer?.display_name}"?
                            </div>

                            <div className="flex justify-end gap-3">
                                <Button onClick={() => setDeleteCustomer(null)}>
                                    {t('customer.cancel')}
                                </Button>

                                <Button
                                    danger
                                    type="primary"
                                   loading={deleteLoading}
                                   onClick={handleDeleteCustomer}
                                >
                                    {t('customer.delete')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Modal>

                <CreateCustomerModal
                    open={openCreate}
                    onClose={() => setOpenCreate(false)}
                    onSubmit={(values) => {
                        console.log("call Submit:", values);
                        fetchCustomers(searchText, customerCareStatusId, page)
                        setStatusRefreshKey((k) => k + 1);
                    }}
                />
                <CreateTaskModal
                    open={openTask}
                    onClose={() => setOpenTask(false)}
                    onSubmit={(values) => console.log(values)}
                />
                <WorkHistoryModal
                    open={openHistory}
                    onClose={() => { setOpenHistory(false); setHistoryCustomerId(null); }}
                    customerId={historyCustomerId}
                />

                <Modal
                    open={openSpamModal}
                    footer={null}
                    onCancel={() => setOpenSpamModal(false)}
                    width={1000}
                    title={
                        <div className="text-[20px] font-bold">
                            Hình ảnh bằng chứng Spam
                        </div>
                    }
                    className="
                        dark:[&_.ant-modal-content]:bg-[#1f1f1f]
                        dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                        dark:[&_.ant-modal-title]:text-white
                    "
                >
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        { selectSpam?.image_1 && (
                            <div
                                className="
                                    overflow-hidden rounded-2xl border
                                    border-slate-200 dark:border-slate-700
                                "
                            >
                                <Image
                                    src={selectSpam.image_1}
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        ) }
                        { selectSpam?.image_2 && (
                            <div
                                className="
                                    overflow-hidden rounded-2xl border
                                    border-slate-200 dark:border-slate-700
                                "
                            >
                                <Image
                                    src={selectSpam.image_2}
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        ) }
                        { selectSpam?.image_3 && (
                            <div
                                className="
                                    overflow-hidden rounded-2xl border
                                    border-slate-200 dark:border-slate-700
                                "
                            >
                                <Image
                                    src={selectSpam.image_3}
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        ) }
                    </div>
                </Modal>
            </div>
        </Layout >
    );
};

export default CustomerCare;